import { Link } from "react-router-dom";
import {
  ArrowRight,
  Zap,
  Users,
  Cloud,
  CheckCircle2,
  Image as ImageIcon,
  Workflow,
  Shield,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { heroDashboard, pixtrayaProduct } from "@/assets/images";
import ArchCarousel from "@/components/ArchCarousel";
import IntegrationsSecurity from "@/components/IntegrationsSecurity";

const HERO_DASH = heroDashboard;
const PIXTRAYA_IMG = pixtrayaProduct;

const VALUE_PROPS = [
  { icon: Workflow, title: "Workflow Automation", desc: "Automate every step from booking to delivery — cut admin time by up to 40%." },
  { icon: Users, title: "Client Management", desc: "Unified client records, communications, and approvals in one place." },
  { icon: ImageIcon, title: "Image Tracking", desc: "Track every frame across culling, editing, review, and final delivery." },
  { icon: Cloud, title: "Cloud-First", desc: "Secure, scalable, accessible from anywhere — studio, set, or on the go." },
];

const HIGHLIGHTS = [
  { k: "Founded 2026", l: "India-headquartered product company" },
  { k: "Cloud-First", l: "Built on modern, scalable infrastructure" },
  { k: "Security-Ready", l: "Encryption & role-based access by design" },
  { k: "Product-Led", l: "Engineered by builders, for operators" },
];

const Home = () => {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.06]"
             style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #0046B5 0, transparent 45%), radial-gradient(circle at 85% 10%, #00C4B5 0, transparent 40%)" }} />
        <div className="container-section grid items-center gap-12 py-16 md:py-24 lg:grid-cols-2 lg:py-32">
          <div className="fade-in-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#0046B5]">
              <Sparkles className="h-3.5 w-3.5" /> Ortelly Systems
            </span>
            <h1 className="mt-5 text-4xl font-bold leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Transforming Digital Workflows with{" "}
              <span className="brand-gradient-text">Innovation</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
              Ortelly Systems Private Limited builds enterprise-grade software products that help modern businesses move faster, collaborate better, and deliver extraordinary customer experiences — powered by our flagship platform, PixTraya.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild data-testid="hero-cta-demo" className="rounded-md bg-[#0046B5] px-6 py-6 text-sm font-medium text-white shadow-sm hover:bg-[#003A99]">
                <Link to="/products#demo">Get a Demo <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" data-testid="hero-cta-contact" className="rounded-md border-slate-200 px-6 py-6 text-sm font-medium text-slate-900 hover:text-[#0046B5]">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
            <div className="mt-10 flex items-center gap-5 text-xs text-slate-500">
              <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#00C4B5]" /> Cloud native</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#00C4B5]" /> Security-first</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#00C4B5]" /> Made in India</div>
            </div>
          </div>
          <div className="relative fade-in-up">
            <div className="absolute -inset-4 -z-10 rounded-2xl brand-gradient opacity-20 blur-2xl hero-glow-anim" />

            {/* Floating accent chips */}
            <div className="hero-float-dot absolute -left-4 top-8 z-20 hidden rounded-md border border-slate-200 bg-white p-3 shadow-lg md:flex md:items-center md:gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#00C4B5]/15 text-[#00C4B5]">
                <CheckCircle2 className="h-4 w-4" />
              </span>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Workflow</p>
                <p className="text-xs font-semibold text-slate-900">Automated</p>
              </div>
            </div>
            <div className="hero-float-dot absolute -right-2 bottom-10 z-20 hidden rounded-md border border-slate-200 bg-white p-3 shadow-lg md:flex md:items-center md:gap-2" style={{ animationDelay: "1.5s" }}>
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#0046B5]/15 text-[#0046B5]">
                <Sparkles className="h-4 w-4" />
              </span>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Delivery</p>
                <p className="text-xs font-semibold text-slate-900">+40% faster</p>
              </div>
            </div>

            <div className="hero-image-anim relative overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl hero-shimmer">
              <img src={HERO_DASH} alt="PixTraya SaaS dashboard" className="h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS (new-firm capability band) */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="container-section py-12 md:py-16">
          <div className="mb-8 max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0046B5]">What we stand for</span>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              A new-generation product company, built for the next decade of creative work.
            </h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {HIGHLIGHTS.map((h) => (
              <div
                key={h.k}
                data-testid={`highlight-${h.k.toLowerCase().replace(/\s+/g,'-')}`}
                className="rounded-md border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#0046B5]/40 hover:shadow-md"
              >
                <p className="font-[Outfit] text-lg font-semibold tracking-tight text-[#0046B5] md:text-xl">{h.k}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{h.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="container-section py-16 md:py-24">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0046B5]">Why Ortelly</span>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Built for teams that move fast, ship beautifully, and scale confidently.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            We build purpose-built platforms that collapse tool sprawl into a single, elegant workflow — starting with photography, extending across creative services.
          </p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {VALUE_PROPS.map((v) => (
            <div key={v.title} data-testid={`value-prop-${v.title.toLowerCase().replace(/\s+/g,'-')}`} className="group rounded-md border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#0046B5]/40 hover:shadow-md">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#0046B5]/10 text-[#0046B5] transition-colors group-hover:bg-[#0046B5] group-hover:text-white">
                <v.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ARCHITECTURE CAROUSEL (auto-slide) */}
      <ArchCarousel />

      {/* INTEGRATIONS & SECURITY */}
      <IntegrationsSecurity />

      {/* PIXTRAYA FEATURE STRIP */}
      <section className="border-y border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="container-section grid items-center gap-12 py-16 md:py-24 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00C4B5]">Flagship Product</span>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Meet <span className="brand-gradient-text">PixTraya</span> — your end-to-end photography workflow platform.
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
              PixTraya unifies bookings, shoot planning, image processing, client review, delivery, and invoicing into a single, beautifully designed workspace — purpose-built for studios and professional photographers.
            </p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {["Booking & shoot planning", "Automated culling pipelines", "Client review & approvals", "Cloud-backed delivery", "Team collaboration", "Invoicing & payments"].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#00C4B5]" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild data-testid="pixtraya-cta-learn" className="rounded-md bg-[#0046B5] text-white hover:bg-[#003A99]">
                <Link to="/products">Learn More <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" data-testid="pixtraya-cta-demo" className="rounded-md border-slate-200 hover:text-[#0046B5]">
                <Link to="/products#demo">Request Demo</Link>
              </Button>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
              <img src={PIXTRAYA_IMG} alt="PixTraya photography workflow" className="h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="container-section py-16 md:py-24">
        <div className="relative overflow-hidden rounded-lg border border-slate-200 bg-slate-900 p-8 md:p-14">
          <div className="absolute inset-0 -z-10 opacity-25 brand-gradient" />
          <div className="relative grid items-center gap-6 lg:grid-cols-2">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                Ready to transform your workflow?
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-200 md:text-base">
                Talk to our product team. We'll walk you through PixTraya, map your workflow, and show you exactly how much time you'll save.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Button asChild data-testid="cta-band-demo" className="rounded-md bg-white text-[#0046B5] hover:bg-slate-100">
                <Link to="/products#demo">Get a Demo</Link>
              </Button>
              <Button asChild variant="outline" data-testid="cta-band-contact" className="rounded-md border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white">
                <Link to="/contact">Contact Sales</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
